import { ITDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { EFFECT_KEY } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import { TEXT_FILL_LIST } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import { IParagraph, ITextRun } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { ITextRunStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';

/**
 * 修改文字颜色
 */
export function setTextRunColor(paragraphList: IParagraph[], tdColor: ITDColor, paths?: Array<number[]>): IParagraph[] {
    if (tdColor?.srgb_color) {
        tdColor.srgb_color = tdColor.srgb_color.toUpperCase?.();
    }
    return setTextRunStyles(paragraphList, { tdColor }, paths);
}

/**
 * 根据新的文字样式生成新的 paragraphList
 * @param paragraphList 原始段落列表
 * @param styles 要修改的 textRun 样式
 * @param paths 描述哪些 textRun 需要修改，每一个 path 是一个数字数组，[paragraphIndex, textRunIndex?]，如果没有 textRunIndex，表示修改当前段落所有 textRun
 *              不传这个参数，表示修改所有段落的 textRun
 * @returns { IParagraph[] }
 */
export function setTextRunStyles(
    paragraphList: IParagraph[],
    styles: Partial<ITextRunStyle>,
    paths?: Array<number[]>,
): IParagraph[] {
    const enhancedStyles = enhanceStyles(styles);
    // 将 paths 转换为一个 Set，其中每个元素是一个字符串，格式为 `${paraIndex},${textRunIndex}`
    const pathSet = new Set(paths?.map(path => path.join(',')));
    return paragraphList.map((para: IParagraph, paraIndex: number) => {
        // 标识当前段所有 textRun 是否都改变了
        let changeAllTextRun = true;
        const newParagraph = {
            ...para,
            textRuns: para.textRuns?.map((textRun, textRunIndex: number) => {
                // 如果没有提供 paths，或者当前的 paraIndex 和 textRunIndex 与 paths 中的某个元素匹配，则更新 textRun
                if (!paths || pathSet.has(`${paraIndex},${textRunIndex}`) || pathSet.has(`${paraIndex}`)) {
                    return updateTextRunStyle(textRun, enhancedStyles);
                }
                changeAllTextRun = false;
                return textRun;
            }),
        };
        // 只有在当前段落中所有 textrun 都要修改时再去更新defaultRunProps
        if (newParagraph.paragraphProps?.defaultRunProps && changeAllTextRun) {
            newParagraph.paragraphProps.defaultRunProps = {
                ...newParagraph.paragraphProps?.defaultRunProps,
                ...enhancedStyles,
            };
        }
        return newParagraph;
    });
}

/**
 * 完善要修改的 styles 对象
 * @param styles
 * @returns
 */
function enhanceStyles(styles: Partial<ITextRunStyle>): Partial<ITextRunStyle> {
    const enhancedStyles = { ...styles };
    if (enhancedStyles.tdColor) {
        enhancedStyles.color = enhancedStyles.tdColor.srgb_color;
        // 设置颜色时清除填充数据
        TEXT_FILL_LIST.forEach((key) => {
            enhancedStyles[key] = undefined;
        });
    }
    return enhancedStyles;
}

/**
 * 把新的 styles 应用给传入的 textRun 并返回
 * @param textRun
 * @param styles
 * @returns
 */
function updateTextRunStyle(textRun: ITextRun, styles: Partial<ITextRunStyle>) {
    if (!textRun.styles) {
        return textRun;
    }
    const newStyles = { ...textRun.styles };
    const { gradFill, image, pattFill, noFill, tdColor } = styles;

    // 给链接设置填充时需要修改链接颜色属性
    if (textRun.styles.href && (gradFill || image || pattFill || noFill || tdColor)) {
        newStyles.href = {
            ...textRun.styles.href,
            colorType: 'tx', // 表示使用字体颜色
        };
        newStyles['raw-href'] = true; // 加上raw-标志，用以提交sdk时区分继承属性
    }
    Object.keys(styles || {}).forEach((key) => {
        if (key === EFFECT_KEY) {
            newStyles[key] = { ...newStyles[key], ...styles.effect };
        } else {
            newStyles[key] = styles[key];
        }
        newStyles[`raw-${key}`] = true; // 加上raw-标志，用以提交sdk时区分继承属性
    });
    return {
        ...textRun,
        styles: newStyles,
    };
}
